/**
 * 매출 단축 등록 API — 매출분석 헤더의 [+ 유지보수] / [+ 비상벨] 버튼.
 *
 * - POST   /maintenance-sales         유지보수 월별 매출 (기존 계약 1개 선택)
 * - PATCH  /maintenance-sales/:id     수정 (계약 변경 불가)
 * - DELETE /maintenance-sales/:id     삭제
 * - POST   /emergency-bell-sales      비상벨 매출 (자유 입력)
 * - PATCH  /emergency-bell-sales/:id  수정
 * - DELETE /emergency-bell-sales/:id  삭제
 */

import { api } from './api';
import type { CompanyId } from './types';

export interface MaintenanceSalesQuickInput {
  contractId: number;
  /** invoiceDate 가 있으면 서버에서 자동 추출. 둘 다 없으면 오늘 날짜. */
  revenueYear?: number;
  revenueMonth?: number;
  salesAmount: number;
  purchaseAmount?: number;
  invoiceDate?: string | null;
  note?: string | null;
  /** 같은 (계약+발행연월) 행이 있어도 강제 추가 (사용자 확인 후). */
  force?: boolean;
}

export type MaintenanceSalesUpdateInput = Partial<
  Omit<MaintenanceSalesQuickInput, 'contractId'>
>;

export async function createMaintenanceSales(
  input: MaintenanceSalesQuickInput,
): Promise<{ maintenanceId: number }> {
  return api.post('/maintenance-sales', input);
}

export async function updateMaintenanceSales(
  id: number,
  input: MaintenanceSalesUpdateInput,
): Promise<{ maintenanceId: number }> {
  return api.patch(`/maintenance-sales/${id}`, input);
}

export async function deleteMaintenanceSales(
  id: number,
): Promise<{ maintenanceId: number }> {
  return api.delete(`/maintenance-sales/${id}`);
}

/** 비상벨 종결여부 (운영 엑셀 4 값). null 은 미상태. */
export type BellStatus = '종결' | '검사검수요청' | '취소' | '매입계산서 보류';

// ── 분납서 PDF 자동 추출 결과 (POST /emergency-bell-sales/parse-procurement-pdf) ──
export interface BellPdfParseResult {
  procurementNo: string | null;
  contractDate:  string | null;  // YYYY-MM-DD
  endUser:       string | null;
  department:    string | null;  // 담당과 — 문서번호 앞부분 (v2.309.0)
  businessName:  string | null;  // → siteName (공사명)
  unitPrice:     number | null;  // 정수 원
  deviceCount:   number | null;
  deliveryDue:   string | null;  // YYYY-MM-DD
}

export async function parseBellProcurementPdf(file: File): Promise<BellPdfParseResult> {
  const fd = new FormData();
  fd.append('file', file);
  const res = await fetch('/api/emergency-bell-sales/parse-procurement-pdf', {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  const json = await res.json() as { ok: true; data: BellPdfParseResult } | { ok: false; error: { message: string } };
  if (!json.ok) throw new Error(json.error.message);
  return json.data;
}

export interface BellSalesQuickInput {
  empId: string;
  companyId: CompanyId;
  siteName?: string | null;
  endUser?: string | null;
  contractCompany?: string | null;
  deviceCount?: number;
  unitPrice?: number;
  salesAmount: number;
  purchaseAmount?: number;
  invoiceDate?: string | null;
  // revenueYear/Month 는 백엔드가 invoice_date 에서 자동 추출 — 옵션
  revenueYear?: number;
  revenueMonth?: number;
  maintenancePeriod?: string | null;
  note?: string | null;
  // ETL 보강 필드 — 모달 [추가 정보 펼치기] 영역.
  procurementNo?: string | null;
  contractDate?: string | null;
  department?: string | null;
  deliveryDue?: string | null;
  startDate?: string | null;
  deliveryCompletedDate?: string | null;
  bellStatus?: BellStatus | null;
  documents?: string | null;
  purchaseInvoiceDate?: string | null;
  paymentDate?: string | null;
  paidDate?: string | null;
}

export type BellSalesUpdateInput = Partial<BellSalesQuickInput>;

export async function createBellSales(
  input: BellSalesQuickInput,
): Promise<{ bellId: number }> {
  return api.post('/emergency-bell-sales', input);
}

export async function updateBellSales(
  id: number,
  input: BellSalesUpdateInput,
): Promise<{ bellId: number }> {
  return api.patch(`/emergency-bell-sales/${id}`, input);
}

export async function deleteBellSales(
  id: number,
): Promise<{ bellId: number }> {
  return api.delete(`/emergency-bell-sales/${id}`);
}
